import React, { useState, useEffect } from 'react';
import { Card, InputNumber, Button, message, Typography, Space, Divider, Alert } from 'antd';
import { SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiMethods } from '../services/api';
import { clearHijriOffsetCache } from '../utils/hijriAdjustment';

const { Title, Text } = Typography;

const HijriOffsetSettings: React.FC = () => {
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load current settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const settings = await apiMethods.getHijriSettings();
        setOffset(settings.hijri_offset);
      } catch (error) {
        console.error('Error loading Hijri settings:', error);
        message.error('Failed to load Hijri settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('Attempting to save Hijri offset:', offset);
      const result = await apiMethods.updateHijriOffset(offset);
      console.log('Save result:', result);
      clearHijriOffsetCache(); // Update the cache immediately
      message.success('Hijri offset updated successfully');
    } catch (error: any) {
      console.error('Error updating Hijri offset:', error);

      // More specific error handling
      if (error?.response?.status === 401) {
        message.error('Authentication error. Please try logging in again.');
      } else if (error?.response?.status === 400) {
        message.error('Invalid offset value. Please check your input.');
      } else {
        message.error(`Failed to update Hijri offset: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setOffset(0);
  };

  return (
    <Card
      title={
        <Space>
          <SettingOutlined />
          <span>Hijri Calendar Adjustment</span>
        </Space>
      }
      loading={loading}
      style={{ maxWidth: 600, margin: '0 auto' }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Alert
          message="About Hijri Date Adjustment"
          description="Islamic dates depend on moon sighting, which may differ from calculated dates. Use this setting to align the displayed Hijri dates with your local observations."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
        />

        <div>
          <Title level={4}>Current Offset</Title>
          <Space align="center" size="middle">
            <Text>Adjust Hijri dates by:</Text>
            <InputNumber
              value={offset}
              onChange={(value) => setOffset(value || 0)}
              min={-10}
              max={10}
              addonAfter="days"
              style={{ width: 120 }}
              disabled={saving}
            />
          </Space>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Range: -10 to +10 days
            </Text>
          </div>
        </div>

        <Divider />

        <div>
          <Title level={5}>Examples:</Title>
          <ul style={{ marginLeft: 16, color: '#666' }}>
            <li><strong>+1 day:</strong> If local sighting confirms the new month is one day ahead of calculations</li>
            <li><strong>-1 day:</strong> If calculations are one day ahead of local sighting</li>
            <li><strong>0 days:</strong> Use calculated dates without adjustment (default)</li>
          </ul>
        </div>

        <Space>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={loading}
          >
            Save Changes
          </Button>
          <Button
            onClick={handleReset}
            disabled={loading || saving}
          >
            Reset to 0
          </Button>
        </Space>

        <Alert
          message="Note: This adjustment will apply to all Hijri dates throughout the application, including the calendar display and event dates."
          type="warning"
          showIcon
        />
      </Space>
    </Card>
  );
};

export default HijriOffsetSettings; 